import { ReactNode } from 'react';
import { useFeatureAccess } from '../hooks/useSubscription';
import { useAuth } from '../contexts/AuthContext';
import { Lock, AlertCircle, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';

interface FeatureGateProps {
  featureKey: string;
  children: ReactNode;
  fallback?: ReactNode;
  showUpgradePrompt?: boolean;
  featureName?: string;
}

export function FeatureGate({
  featureKey,
  children,
  fallback,
  showUpgradePrompt = true,
  featureName,
}: FeatureGateProps) {
  const { isAdmin } = useAuth();
  const { hasAccess, loading } = useFeatureAccess(featureKey);

  // ADMIN BYPASS: Admins always have access
  if (isAdmin) {
    return <>{children}</>;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (hasAccess === true) {
    return <>{children}</>;
  }

  if (fallback) {
    return <>{fallback}</>;
  }

  if (showUpgradePrompt) {
    return (
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-8 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
          <Lock className="text-blue-600" size={32} />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">
          {featureName ? `${featureName} is a Premium Feature` : 'Premium Feature'}
        </h3>
        <p className="text-gray-600 mb-6 max-w-md mx-auto">
          Upgrade your subscription to unlock this feature and get access to advanced career
          development tools.
        </p>
        <Link
          to="/subscription"
          className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-semibold"
        >
          View Plans & Upgrade
        </Link>
      </div>
    );
  }

  return null;
}

interface QuotaGateProps {
  quotaType: 'ai_coach_messages' | 'job_match_analyses' | 'report_runs';
  children: ReactNode;
  fallback?: ReactNode;
  featureName?: string;
}

export function QuotaGate({ quotaType, children, fallback, featureName }: QuotaGateProps) {
  const { isAdmin } = useAuth();
  const { quota, loading, isLimitReached } = useQuota(quotaType);

  // ADMIN BYPASS: Admins have unlimited quota
  if (isAdmin) {
    return <>{children}</>;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!isLimitReached) {
    return <>{children}</>;
  }

  if (fallback) {
    return <>{fallback}</>;
  }

  return (
    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8 text-center">
      <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-100 rounded-full mb-4">
        <AlertCircle className="text-yellow-600" size={32} />
      </div>
      <h3 className="text-2xl font-bold text-gray-900 mb-2">
        {featureName ? `${featureName} Limit Reached` : 'Monthly Limit Reached'}
      </h3>
      <p className="text-gray-600 mb-4 max-w-md mx-auto">
        You've used all {quota?.limit} of your monthly {featureName?.toLowerCase() || 'quota'}.
      </p>
      {quota?.resets_at && (
        <p className="text-sm text-gray-500 mb-6">
          Your quota resets on {new Date(quota.resets_at).toLocaleDateString()}
        </p>
      )}
      <Link
        to="/subscription"
        className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-semibold"
      >
        Upgrade for More
      </Link>
    </div>
  );
}

import { useQuota } from '../hooks/useSubscription';
